import {z} from 'zod/v4';
import {objectIdStringZ4, optionalNonNullZ4} from '@packages/utilities/zod';
import {AeoAssetCountSummary, Dateish} from './shared';

// --- Sub-schemas for Brand entity ---

export const organizationTypes = [
  'corporation',
  'llc',
  'nonprofit',
  'partnership',
  'sole_proprietorship',
  'other',
] as const;
export const OrganizationType = z.enum(organizationTypes);
export type OrganizationType = z.infer<typeof OrganizationType>;

export const Address = z.object({
  street: optionalNonNullZ4(z.string()),
  city: optionalNonNullZ4(z.string()),
  state: optionalNonNullZ4(z.string()),
  postalCode: optionalNonNullZ4(z.string()),
  country: optionalNonNullZ4(z.string()),
});
export type Address = z.infer<typeof Address>;

export const OrganizationContact = z.object({
  email: optionalNonNullZ4(z.string()),
  telephone: optionalNonNullZ4(z.string()),
});
export type OrganizationContact = z.infer<typeof OrganizationContact>;

export const OrganizationAbout = z.object({
  foundingDate: optionalNonNullZ4(Dateish),
  organizationType: optionalNonNullZ4(OrganizationType),
  industry: optionalNonNullZ4(z.string()),
  description: optionalNonNullZ4(z.string()),
});
export type OrganizationAbout = z.infer<typeof OrganizationAbout>;

export const Location = z.object({
  name: z.string(),
  isHeadquarters: z.boolean().default(false),
  address: Address,
});
export type Location = z.infer<typeof Location>;

export const OrganizationPerson = z.object({
  name: z.string(),
  role: optionalNonNullZ4(z.string()),
});
export type OrganizationPerson = z.infer<typeof OrganizationPerson>;

export const competitorDomainUrl = z
  .string()
  .url('Must be a valid URL (e.g. https://competitor.com)')
  .refine((val) => {
    try {
      const url = new URL(val);
      return (
        url.protocol === 'https:' &&
        url.hostname.includes('.') &&
        (url.pathname === '/' || url.pathname === '') &&
        !url.search &&
        !url.hash
      );
    } catch {
      return false;
    }
  }, 'Must be an HTTPS URL with no path (e.g. https://competitor.com)');

// Lenient read schema — accepts legacy bare hostnames alongside validated HTTPS URLs
export const Competitor = z.object({
  name: z.string(),
  aliases: z.array(z.string()).default([]),
  domains: z.array(z.string()).default([]),
});
export type Competitor = z.infer<typeof Competitor>;

// Strict write schema — enforces HTTPS-only URLs for new data
export const CompetitorInput = z.object({
  name: z.string(),
  aliases: z.array(z.string()).default([]),
  domains: z.array(competitorDomainUrl).default([]),
});
export type CompetitorInput = z.infer<typeof CompetitorInput>;

/**
 * Status values for a Brand entity.
 */
export const brandStatus = ['active', 'inactive', 'archived'] as const;

export const BrandStatus = z.enum(brandStatus);
export type BrandStatus = z.infer<typeof BrandStatus>;

/**
 * Brand entity for storing metadata needed for matching and reporting in LLM responses.
 * Site-scoped, one brand per site in Alpha.
 */
export const Brand = z
  .object({
    id: objectIdStringZ4,
    siteId: objectIdStringZ4,
    name: z.string().min(1).max(255),
    description: optionalNonNullZ4(z.string().max(1000)),
    aliases: z.array(z.string().max(255)).default([]),
    status: BrandStatus.default('active'),
    contact: optionalNonNullZ4(OrganizationContact),
    about: optionalNonNullZ4(OrganizationAbout),
    locations: z.array(Location).default([]),
    people: z.array(OrganizationPerson).default([]),
    competitors: z.array(Competitor).default([]),
    createdAt: Dateish,
    updatedAt: optionalNonNullZ4(Dateish),
  })
  .describe(
    'Brand entity storing metadata for matching and reporting in LLM responses.'
  );
export type Brand = z.infer<typeof Brand>;

/**
 * Input schema for creating a new Brand.
 * Uses CompetitorInput to enforce HTTPS-only domain URLs on writes.
 */
export const CreateBrand = Brand.pick({
  name: true,
  description: true,
  aliases: true,
  status: true,
  contact: true,
  about: true,
  locations: true,
  people: true,
})
  .extend({competitors: z.array(CompetitorInput).default([])})
  .partial({
    description: true,
    aliases: true,
    status: true,
    contact: true,
    about: true,
    locations: true,
    people: true,
    competitors: true,
  });
export type CreateBrand = z.infer<typeof CreateBrand>;

/**
 * Input schema for updating a Brand.
 * Uses CompetitorInput to enforce HTTPS-only domain URLs on writes.
 */
export const UpdateBrand = Brand.pick({
  name: true,
  description: true,
  aliases: true,
  status: true,
  contact: true,
  about: true,
  locations: true,
  people: true,
})
  .extend({competitors: z.array(CompetitorInput).default([])})
  .partial();
export type UpdateBrand = z.infer<typeof UpdateBrand>;

/**
 * Response schema for Brand with basic topic info.
 */
export const BrandWithTopicSummary = Brand.extend({
  topicSummary: optionalNonNullZ4(AeoAssetCountSummary),
});
export type BrandWithTopicSummary = z.infer<typeof BrandWithTopicSummary>;
